import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, ArrowRight, RotateCcw, Check } from 'lucide-react';
import AgencyNav from './AgencyNav';
import AgencyFooter from './AgencyFooter';
import Seo from './Seo';
import SignalCta from './SignalCta';
import AssessGauge from './AssessGauge';
import { assessCopy, recommendEngagement, buildPlan } from '../../content/assessCopy';
import { contact } from '../../content/agencyCopy';

const API = process.env.REACT_APP_BACKEND_URL;

const step = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0 },
  exit:    { opacity: 0, x: -24 },
  transition: { duration: 0.28, ease: [0.2, 0.7, 0.2, 1] },
};

export default function Assess() {
  const questions = assessCopy.questions;
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState('intro'); // intro | quiz | submitting | results
  const [plan, setPlan] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);

  const current = questions[i];
  const total = questions.length;

  const canAdvance = useMemo(() => {
    if (!current) return false;
    if (current.type === 'text') return true;
    return Boolean(answers[current.key]);
  }, [current, answers]);

  const pickOption = (value) => {
    setAnswers((prev) => ({ ...prev, [current.key]: value }));
    if (current.type !== 'text' && i < total - 1) {
      setTimeout(() => setI(i + 1), 180);
    }
  };

  const setTextAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [current.key]: value.slice(0, current.maxLength || 1000) }));
  };

  const goBack = () => {
    if (i > 0) setI(i - 1);
    else setPhase('intro');
  };

  const goForward = async () => {
    if (i < total - 1) {
      setI(i + 1);
      return;
    }
    setPhase('submitting');
    // Hold on the analysing screen long enough for it to read as work being
    // done rather than a flash — the request itself is usually faster.
    const settle = new Promise((r) => setTimeout(r, 2000));
    let serverRec = null;
    try {
      const res = await fetch(`${API}/api/assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...answers, source: 'assess-page' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      serverRec = data.recommendation || null;
      if (data.id) setAssessmentId(data.id);
    } catch {
      // Falling back to the client rules keeps the respondent's result intact
      // even when the API is unreachable. Sentry catches the miss server-side.
      serverRec = recommendEngagement(answers);
    }
    await settle;
    setPlan(buildPlan(answers, serverRec));
    setPhase('results');
  };

  const restart = () => {
    setI(0);
    setAnswers({});
    setPlan(null);
    setAssessmentId(null);
    setPhase('intro');
  };

  const progressPct = phase === 'quiz' ? Math.round(((i + 1) / total) * 100) : 0;

  return (
    <div className="fa-scope min-h-screen">
      <Seo
        title="5-minute AI diagnostic · FRONTECH"
        description="Seven questions about how your business runs. Get a written read on where AI fits, which systems match, and whether it is worth building yet — free, no sign-up."
        path="/assess"
      />
      <AgencyNav />

      <main className="fa-grid pt-32 md:pt-40 pb-24 px-6" style={{ background: 'var(--fa-paper)' }}>
        <div className={phase === 'results' ? 'max-w-[880px] mx-auto' : 'max-w-[720px] mx-auto'}>
          {phase === 'quiz' && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <span className="fa-ref fa-ref--signal">{current?.ref}</span>
                <span className="fa-ref" style={{ fontSize: 9 }}>
                  {assessCopy.progressLabel} {i + 1} / {total}
                </span>
              </div>
              <div className="h-[3px] overflow-hidden" style={{ background: 'var(--fa-rule)' }} aria-hidden="true">
                <motion.div
                  className="h-full"
                  style={{ background: 'var(--fa-signal)' }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {phase === 'intro' && (
              <motion.section key="intro" {...step}>
                <div className="fa-label flex items-center gap-3 mb-4">
                  <span className="fa-status-dot fa-status-dot--live" aria-hidden="true" />
                  {assessCopy.intro.eyebrow}
                </div>
                <h1
                  className="font-heading text-[32px] md:text-[46px] font-bold leading-[1.08]"
                  style={{ color: 'var(--fa-ink)', letterSpacing: '-0.02em', textWrap: 'balance' }}
                >
                  {assessCopy.intro.headline}
                </h1>
                <p className="mt-6 text-[16px] md:text-[17px] fa-glow-text" style={{ maxWidth: '62ch' }}>
                  {assessCopy.intro.body}
                </p>
                <ul className="mt-8 space-y-2.5">
                  {assessCopy.intro.reassure.map((r) => (
                    <li key={r} className="flex items-start gap-2.5">
                      <Check size={14} className="mt-[3px] flex-shrink-0" style={{ color: 'var(--fa-signal)' }} />
                      <span className="text-[14.5px] fa-glow-text fa-glow-text--muted">{r}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10">
                  <SignalCta onClick={() => setPhase('quiz')}>
                    {assessCopy.intro.startCta}
                  </SignalCta>
                </div>
              </motion.section>
            )}

            {phase === 'quiz' && current && (
              <motion.section key={`q-${i}`} {...step}>
                <h2
                  className="font-heading text-[24px] md:text-[31px] font-semibold leading-snug"
                  style={{ color: 'var(--fa-ink)', textWrap: 'balance' }}
                >
                  {current.label}
                </h2>
                {current.hint && (
                  <p className="mt-3 text-[14px]" style={{ color: 'var(--fa-muted)' }}>
                    {current.hint}
                  </p>
                )}

                {current.type === 'text' ? (
                  <div className="mt-8">
                    <textarea
                      value={answers[current.key] || ''}
                      onChange={(e) => setTextAnswer(e.target.value)}
                      placeholder={current.placeholder}
                      rows={5}
                      className="w-full text-[15px] p-4 fa-glow-text"
                      style={{
                        background: 'var(--fa-surface)',
                        border: '1px solid var(--fa-rule)',
                        borderRadius: 5,
                        color: 'var(--fa-ink)',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                    <div className="mt-2 text-right fa-ref" style={{ fontSize: 9 }}>
                      {(answers[current.key] || '').length} / {current.maxLength}
                    </div>
                  </div>
                ) : (
                  <div className="mt-8 flex flex-col gap-2.5">
                    {current.options.map((opt, oi) => {
                      const selected = answers[current.key] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => pickOption(opt.value)}
                          className="text-left flex items-center gap-3 px-4 py-3.5 text-[15px] transition-colors"
                          style={{
                            background: selected ? 'var(--fa-signal-wash)' : 'var(--fa-surface)',
                            border: `1px solid ${selected ? 'var(--fa-signal)' : 'var(--fa-rule)'}`,
                            borderRadius: 5,
                            color: selected ? 'var(--fa-signal)' : 'var(--fa-ink)',
                            fontWeight: selected ? 600 : 400,
                          }}
                        >
                          <span
                            className="fa-ref flex-shrink-0"
                            style={{ fontSize: 9, color: selected ? 'var(--fa-signal)' : 'var(--fa-muted)' }}
                            aria-hidden="true"
                          >
                            {String.fromCharCode(65 + oi)}
                          </span>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="mt-10 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-2 text-[14px] px-3 py-2 transition-colors"
                    style={{ color: 'var(--fa-muted)' }}
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  {(current.type === 'text' || (canAdvance && i === total - 1)) && (
                    <SignalCta onClick={goForward} size={i === total - 1 ? undefined : 'sm'}>
                      {i === total - 1 ? 'See the result' : 'Next'}
                    </SignalCta>
                  )}
                </div>
              </motion.section>
            )}

            {phase === 'submitting' && <Analysing key="submitting" />}

            {phase === 'results' && plan && (
              <ResultsView
                key="results"
                plan={plan}
                answers={answers}
                assessmentId={assessmentId}
                onRestart={restart}
              />
            )}
          </AnimatePresence>
        </div>
      </main>

      <AgencyFooter />
    </div>
  );
}

/* Analysing screen — the steps tick through so the wait reads as work. */
function Analysing() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setN((v) => Math.min(v + 1, assessCopy.analysing.length)), 480);
    return () => clearInterval(t);
  }, []);
  return (
    <motion.section {...step}>
      <div className="fa-label flex items-center gap-3">
        <span className="fa-status-dot fa-status-dot--live" aria-hidden="true" />
        Working
      </div>
      <ul className="mt-8 space-y-3" aria-live="polite">
        {assessCopy.analysing.map((line, idx) => (
          <li key={line} className="flex items-center gap-3">
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 15, height: 15, borderRadius: 3,
                background: idx < n ? 'var(--fa-signal)' : 'transparent',
                border: idx < n ? 'none' : '1px solid var(--fa-rule)',
                color: 'var(--fa-paper)',
              }}
            >
              {idx < n && <Check size={10} strokeWidth={3} />}
            </span>
            <span
              className="text-[15px] transition-colors"
              style={{ color: idx < n ? 'var(--fa-ink)' : 'var(--fa-muted)' }}
            >
              {line}
            </span>
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

function ResultsView({ plan, answers, assessmentId, onRestart }) {
  const primary = assessCopy.results[plan.engagement];
  const retainer = plan.withRetainer ? assessCopy.results.retainer : null;

  const whatsappHref = `${contact.whatsappHref}?text=${encodeURIComponent(
    `Hi — I took the diagnostic and got matched to "${primary?.title}" (readiness ${plan.score}). Can we book a call?`
  )}`;
  const calConfig = JSON.stringify({
    name: '',
    email: '',
    notes:
      `Diagnostic: ${primary?.title} (${plan.engagement}${plan.withRetainer ? ' + retainer' : ''}) · ` +
      `readiness ${plan.score}/${100} (${plan.bandLabel}) · answers ${JSON.stringify(answers)}`,
  });

  return (
    <motion.section {...step}>
      <div className="fa-label flex items-center gap-3 mb-4">
        <span className="fa-status-dot fa-status-dot--live" aria-hidden="true" />
        Your read
      </div>

      {/* The sentence that proves we read their answers */}
      <p
        className="text-[17px] md:text-[19px] fa-glow-text leading-relaxed"
        style={{ maxWidth: '60ch', color: 'var(--fa-ink)' }}
      >
        {plan.situation}
      </p>

      <div className="mt-8">
        <AssessGauge
          score={plan.score}
          bandKey={plan.bandKey}
          bandLabel={plan.bandLabel}
          bandNote={plan.bandNote}
        />
      </div>

      {/* Matched systems */}
      <div className="mt-10">
        <div className="fa-label fa-header-rule mb-5">
          <span>Systems that fit</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {plan.systems.map((s, idx) => (
            <motion.div
              key={s.ref}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.25 + idx * 0.08 }}
              className="fa-plate fa-ticks p-4"
            >
              <div className="flex items-center justify-between">
                <span className="fa-ref fa-ref--signal">{s.ref}</span>
                {idx === 0 && <span className="fa-ref" style={{ fontSize: 8.5 }}>Start here</span>}
              </div>
              <h3 className="font-heading text-[15px] font-semibold mt-3 leading-snug" style={{ color: 'var(--fa-ink)' }}>
                {s.name}
              </h3>
              <p className="mt-2 text-[13px] fa-glow-text fa-glow-text--muted">{s.line}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Prior-attempt acknowledgement */}
      {plan.priorNote && (
        <div
          className="mt-8 px-5 py-4"
          style={{ borderLeft: '2px solid var(--fa-signal)', background: 'var(--fa-surface)', borderRadius: 5 }}
        >
          <span className="fa-ref fa-ref--signal">On your last attempt</span>
          <p className="mt-2 text-[14.5px] fa-glow-text" style={{ maxWidth: '62ch' }}>
            {plan.priorNote}
          </p>
        </div>
      )}

      {/* The recommendation */}
      <div className="mt-12 fa-plate fa-ticks p-6 md:p-8" style={{ borderColor: 'var(--fa-signal)' }}>
        <div className="flex items-center justify-between">
          <span className="fa-ref fa-ref--signal">Recommended</span>
          <span className="fa-ref" style={{ fontSize: 9 }}>{primary?.subtitle}</span>
        </div>
        <h2
          className="font-heading text-[28px] md:text-[36px] font-bold mt-4 leading-[1.1]"
          style={{ color: 'var(--fa-ink)', letterSpacing: '-0.02em' }}
        >
          {primary?.title}
        </h2>
        <p className="mt-5 text-[16px] fa-glow-text" style={{ maxWidth: '62ch' }}>
          {primary?.body}
        </p>
        <p
          className="fa-leader mt-7"
          style={{ color: 'var(--fa-ink)', textTransform: 'none', letterSpacing: '0.01em', fontSize: '14px', maxWidth: '58ch' }}
        >
          {plan.firstMove}
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-5">
          <SignalCta
            data-cal-link={contact.calLink}
            data-cal-namespace={contact.calNamespace}
            data-cal-config={calConfig}
          >
            {primary?.cta}
          </SignalCta>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[14px] transition-colors hover:opacity-80"
            style={{ color: 'var(--fa-muted)' }}
          >
            {contact.whatsappSecondaryLabel}
            <span aria-hidden="true" style={{ color: 'var(--fa-signal)' }}>→</span>
          </a>
        </div>
      </div>

      {retainer && (
        <div
          className="mt-5 p-5"
          style={{ background: 'var(--fa-signal-wash)', border: '1px solid var(--fa-signal)', borderRadius: 5 }}
        >
          <span className="fa-ref fa-ref--signal">Also worth knowing</span>
          <div className="font-heading text-[16px] font-semibold mt-2" style={{ color: 'var(--fa-ink)' }}>
            {retainer.title}
          </div>
          <p className="mt-2 text-[14px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '62ch' }}>
            {retainer.body}
          </p>
        </div>
      )}

      <CaptureBrief assessmentId={assessmentId} />

      <div className="mt-10 flex items-center gap-5">
        <button
          type="button"
          onClick={onRestart}
          className="inline-flex items-center gap-2 text-[14px] px-3 py-2 transition-colors"
          style={{ color: 'var(--fa-muted)' }}
        >
          <RotateCcw size={13} /> Retake
        </button>
      </div>

      <details className="mt-8 pt-6" style={{ borderTop: '1px solid var(--fa-rule)' }}>
        <summary className="fa-label cursor-pointer flex items-center gap-2" style={{ color: 'var(--fa-muted)' }}>
          Your answers <ArrowRight size={12} />
        </summary>
        <dl className="mt-4 space-y-2 text-[13px]">
          {Object.entries(answers).map(([k, v]) => (
            <div key={k} className="flex gap-3">
              <dt className="fa-ref" style={{ minWidth: 90 }}>{k}</dt>
              <dd style={{ color: 'var(--fa-ink)' }}>{String(v)}</dd>
            </div>
          ))}
        </dl>
      </details>
    </motion.section>
  );
}

/*
 * Soft email gate. The full result is already visible above this — the email
 * buys a written brief they can forward, not access to their own answers.
 */
function CaptureBrief({ assessmentId }) {
  const c = assessCopy.capture;
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [state, setState] = useState('idle'); // idle | sending | done | error

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setState('sending');
    try {
      const res = await fetch(`${API}/api/assessment/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, assessment_id: assessmentId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState('done');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div
        className="mt-10 flex items-center gap-3 px-5 py-4"
        style={{ background: 'var(--fa-signal-wash)', border: '1px solid var(--fa-signal)', borderRadius: 5 }}
      >
        <Check size={16} style={{ color: 'var(--fa-signal)' }} />
        <span className="text-[15px]" style={{ color: 'var(--fa-signal)' }}>{c.success}</span>
      </div>
    );
  }

  return (
    <div className="fa-plate fa-ticks mt-10 p-6" style={{ background: 'var(--fa-surface)' }}>
      <span className="fa-ref fa-ref--signal">{c.eyebrow}</span>
      <h3 className="font-heading text-[19px] font-semibold mt-3" style={{ color: 'var(--fa-ink)' }}>
        {c.headline}
      </h3>
      <p className="mt-2 text-[14.5px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '58ch' }}>
        {c.body}
      </p>

      <form onSubmit={submit} className="mt-6 grid gap-3 sm:grid-cols-3">
        {[
          { k: 'name',    ph: c.namePlaceholder,    type: 'text',  required: false },
          { k: 'email',   ph: c.emailPlaceholder,   type: 'email', required: true  },
          { k: 'company', ph: c.companyPlaceholder, type: 'text',  required: false },
        ].map((f) => (
          <input
            key={f.k}
            type={f.type}
            required={f.required}
            value={form[f.k]}
            onChange={(e) => setForm((p) => ({ ...p, [f.k]: e.target.value }))}
            placeholder={f.ph}
            aria-label={f.ph}
            className="text-[14.5px] px-3.5 py-3"
            style={{
              background: 'var(--fa-paper)',
              border: '1px solid var(--fa-rule)',
              borderRadius: 5,
              color: 'var(--fa-ink)',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        ))}
        <div className="sm:col-span-3 flex flex-wrap items-center gap-4 mt-1">
          <SignalCta type="submit" size="sm" disabled={state === 'sending'}>
            {state === 'sending' ? 'Sending…' : c.cta}
          </SignalCta>
          <span className="fa-ref" style={{ fontSize: 9, textTransform: 'none', letterSpacing: '0.02em' }}>
            {c.privacy}
          </span>
        </div>
        {state === 'error' && (
          <p className="sm:col-span-3 text-[13.5px]" style={{ color: 'var(--fa-muted)' }}>{c.error}</p>
        )}
      </form>
    </div>
  );
}
